import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import { useDispatch } from 'react-redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { fetchSettingByCode } from 'APIPackage/AdminModule/services/adminCatalogsExportsApi';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { makeMaxNumber } from 'CorePackage/MainModule/validators/maxNumber';
import { makeMinNumber } from 'CorePackage/MainModule/validators/minNumber';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { isNumberRegexp } from 'CorePackage/MainModule/validators/isNumberRegexp';

import styles from './AdminAutosaveSettings.module.scss';
import { adminUpdateSettings } from '../actions/adminAction';

interface IAdminAutosaveSettingsForm {
  enable: boolean;
  period?: number;
}

export type IAdminAutosaveSettingsFormRenderProps = FormRenderProps<IAdminAutosaveSettingsForm>;

const validateForm = (values: IAdminAutosaveSettingsForm) => {
  return {
    period: values.enable
      ? composeValidators(
          makeRequired(REQUIRED_ERROR),
          isNumberRegexp('В поле можно ввести только цифры'),
          makeMinNumber(1, 'Число не может быть меньше 1'),
          makeMaxNumber(60, 'Число не может быть больше 60'),
        )(values.period)
      : undefined,
  };
};

export const AdminAutosaveSettings = () => {
  const dispatch = useDispatch();
  const [autosaveSetting, setAutosaveSetting] = useState<IAdminSetting>();
  const [autosaveSettingPeriod, setAutosaveSettingPeriod] = useState<IAdminSetting>();
  const formRef = useRef<FormApi<IAdminAutosaveSettingsForm, Partial<IAdminAutosaveSettingsForm>>>();

  useEffect(() => {
    fetchSettingByCode(AdminSettingCodesEnum.PASSPORT_AUTOSAVE).then(setting => {
      setAutosaveSetting(setting);
      formRef.current?.change('enable', setting.value !== undefined ? setting.value === 'true' : undefined);
    });
    fetchSettingByCode(AdminSettingCodesEnum.PASSPORT_AUTOSAVE_PERIOD_MINUTES).then(setting => {
      setAutosaveSettingPeriod(setting);
      formRef.current?.change('period', setting.value ? parseInt(setting.value, 10) : undefined);
    });
  }, []);

  const handleFormSubmit = useCallback(
    (values: IAdminAutosaveSettingsForm) => {
      if (autosaveSetting && autosaveSettingPeriod) {
        dispatch(adminUpdateSettings({ ...autosaveSetting, value: values.enable.toString() }, autosaveSetting.id));
        dispatch(
          adminUpdateSettings(
            { ...autosaveSettingPeriod, value: values.period ? values.period.toString() : undefined },
            autosaveSettingPeriod.id,
          ),
        );
      }
    },
    [autosaveSetting, autosaveSettingPeriod],
  );

  const renderForm = ({ handleSubmit, values, form }: IAdminAutosaveSettingsFormRenderProps) => {
    formRef.current = form;

    return (
      <div className={styles.Wrapper}>
        <div className={styles.Title}>Настройка автоматического сохранения паспортов ЕРКНМ</div>
        <form className={styles.Form} onSubmit={handleSubmit}>
          <Row>
            <FieldBlock
              size="half"
              label="Автосохранение"
              placementLabel="left"
              color="black"
              className={styles.CheckboxWrapper}
            >
              <CheckboxField id="enable" name="enable" type="checkbox" theme="system" size="medium" />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label={'Период автосохранения (мин)'} placementLabel="left" color="black">
              <TextInputField
                id="period"
                name="period"
                type="number"
                placeholder={'Период'}
                disabled={!values.enable}
              />
            </FieldBlock>
          </Row>
          <Row>
            <Button
              disabled={false}
              color="primary"
              type="button"
              variant="contained"
              onClick={handleSubmit}
              text="Сохранить"
            />
          </Row>
        </form>
      </div>
    );
  };

  return (
    <Form<IAdminAutosaveSettingsForm>
      initialValues={{ enable: false, period: undefined }}
      render={renderForm}
      onSubmit={handleFormSubmit}
      validate={validateForm}
    />
  );
};
